import { useCallback, useEffect, useRef } from 'react';
import { useMount, useUnmount } from 'react-use';
import { match } from 'ts-pattern';

import type { PrevisitInsightsStatus } from '@eluve/api-contract';
import { AppointmentStatusEnum } from '@eluve/graphql-types';
import { useAnalytics } from '@eluve/posthog-react';
import { type InsightsPanelView } from '@eluve/sdui-renderer';
import { type Spec, getRootChildKeys } from '@eluve/sdui-types';

export type { InsightsPanelView };

export const getInsightsViewFromAppointmentStatus = (
  status: AppointmentStatusEnum | null | undefined,
): InsightsPanelView | undefined => {
  const view = match(status)
    .with('NOT_STARTED', () => 'not_started' as const)
    .with('ACTIVE', () => 'recording' as const)
    .with('COMPLETED', () => 'summary' as const)
    .otherwise(() => undefined);

  return view;
};

/**
 * Represents the current analytics state of the InsightsPanel.
 * This can be used by parent components to capture the state at specific moments
 * (e.g., when recording starts).
 */
export interface InsightsAnalyticsState {
  /** Whether the insights panel is visible */
  isVisible: boolean;
  /** Whether insights have finished loading (success or error) */
  hasLoaded: boolean;
  /** Whether insights loaded successfully with content */
  loadedSuccessfully: boolean;
  /** Time in ms since insights loaded (null if not loaded yet) */
  timeSinceLoadedMs: number | null;
  /** Total time viewing the panel in ms */
  totalViewDurationMs: number;
  /** Number of sections that were expanded */
  sectionsExpandedCount: number;
  /** Total time spent with sections expanded in ms */
  timeExpandedMs: number;
  /** Maximum scroll depth reached (0-100) */
  maxScrollDepthPercent: number;
  /** Whether user scrolled to the bottom */
  reachedBottom: boolean;
  /** Number of link clicks */
  linkClickCount: number;
}

interface UseInsightsPanelAnalyticsOptions {
  appointmentId: string;
  spec: Spec | null;
  error?: string | null;
  /** Backend-derived status — undefined until the first response arrives */
  status?: PrevisitInsightsStatus;
  /** Whether the spec is actually rendered in the DOM (not hidden behind a loading/transition state) */
  isContentRendered: boolean;
  view?: InsightsPanelView;
}

interface UseInsightsPanelAnalyticsResult {
  /** DOM ref for the container element (for scroll tracking) */
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** DOM ref for the bottom marker element (for scroll depth tracking) */
  bottomMarkerRef: React.RefObject<HTMLDivElement | null>;
  /** Get the current analytics state snapshot */
  getAnalyticsState: () => InsightsAnalyticsState;
  /** Increment sections expanded count (called by renderers via window event) */
  incrementSectionsExpanded: () => void;
  /** Add time to total expanded time */
  addExpandedTime: (ms: number) => void;
  /** Register an expanded section for real-time tracking; returns unregister function */
  registerExpandedSection: () => () => void;
  /** Increment link click count */
  incrementLinkClicks: () => void;
  /** Get time since loaded for event properties */
  getTimeSinceLoaded: () => number | null;
}

/**
 * Finds the nearest scrollable parent element by traversing up the DOM tree.
 */
const getScrollableParent = (
  element: HTMLElement | null,
): HTMLElement | null => {
  if (!element) return null;

  let parent = element.parentElement;
  while (parent) {
    const { overflow, overflowY } = getComputedStyle(parent);
    if (
      overflow === 'auto' ||
      overflow === 'scroll' ||
      overflowY === 'auto' ||
      overflowY === 'scroll'
    ) {
      return parent;
    }
    parent = parent.parentElement;
  }

  return null;
};

/**
 * Custom hook for InsightsPanel lifecycle analytics.
 * Handles: viewed, loaded, view_ended events and scroll tracking.
 * Component-level events (expand/collapse/click) are handled by the data-driven
 * analytics in the renderers.
 */
export const useInsightsPanelAnalytics = ({
  appointmentId,
  spec,
  error,
  status,
  isContentRendered,
  view,
}: UseInsightsPanelAnalyticsOptions): UseInsightsPanelAnalyticsResult => {
  const { capture } = useAnalytics('insights');

  // Lifecycle refs
  const mountTimeRef = useRef<number>(Date.now());
  const loadStartTimeRef = useRef<number>(Date.now());
  const loadedAtRef = useRef<number | null>(null);
  const insightsLoadedRef = useRef(false);
  const loadedSuccessfullyRef = useRef(false);

  // Interaction tracking refs (updated by renderers)
  const sectionsExpandedCountRef = useRef(0);
  const totalTimeExpandedRef = useRef(0);
  const linkClickCountRef = useRef(0);

  // Track "any section expanded" state for time calculation
  // We only care about whether at least one section is open, not the sum of all
  const expandedSectionCountRef = useRef(0);
  const anyExpandedSinceRef = useRef<number | null>(null);

  // Scroll tracking refs
  const lifetimeMaxScrollDepthRef = useRef(0);
  const everReachedBottomRef = useRef(false);

  // DOM refs
  const containerRef = useRef<HTMLDivElement>(null);
  const bottomMarkerRef = useRef<HTMLDivElement>(null);

  const getTimeSinceLoaded = useCallback((): number | null => {
    return loadedAtRef.current ? Date.now() - loadedAtRef.current : null;
  }, []);

  const getAnalyticsState = useCallback((): InsightsAnalyticsState => {
    const now = Date.now();

    // Calculate current expanded time including in-progress time if any section is open
    let currentTimeExpandedMs = totalTimeExpandedRef.current;
    if (anyExpandedSinceRef.current !== null) {
      currentTimeExpandedMs += now - anyExpandedSinceRef.current;
    }

    return {
      isVisible: true,
      hasLoaded: insightsLoadedRef.current,
      loadedSuccessfully: loadedSuccessfullyRef.current,
      timeSinceLoadedMs: loadedAtRef.current ? now - loadedAtRef.current : null,
      totalViewDurationMs: now - mountTimeRef.current,
      sectionsExpandedCount: sectionsExpandedCountRef.current,
      timeExpandedMs: currentTimeExpandedMs,
      maxScrollDepthPercent: lifetimeMaxScrollDepthRef.current,
      reachedBottom: everReachedBottomRef.current,
      linkClickCount: linkClickCountRef.current,
    };
  }, []);

  const incrementSectionsExpanded = useCallback(() => {
    sectionsExpandedCountRef.current += 1;
  }, []);

  const addExpandedTime = useCallback((ms: number) => {
    totalTimeExpandedRef.current += ms;
  }, []);

  const registerExpandedSection = useCallback(() => {
    // First section expanded - start tracking time
    if (expandedSectionCountRef.current === 0) {
      anyExpandedSinceRef.current = Date.now();
    }
    expandedSectionCountRef.current += 1;

    return () => {
      expandedSectionCountRef.current -= 1;
      // Last section collapsed - stop tracking and add elapsed time
      if (
        expandedSectionCountRef.current === 0 &&
        anyExpandedSinceRef.current !== null
      ) {
        totalTimeExpandedRef.current +=
          Date.now() - anyExpandedSinceRef.current;
        anyExpandedSinceRef.current = null;
      }
    };
  }, []);

  const incrementLinkClicks = useCallback(() => {
    linkClickCountRef.current += 1;
  }, []);

  // Track viewed on mount
  useMount(() => {
    mountTimeRef.current = Date.now();
    loadStartTimeRef.current = Date.now();
    capture('viewed', { appointmentId, view });
  });

  // Track view_ended on unmount
  useUnmount(() => {
    const now = Date.now();
    capture('view_ended', {
      appointmentId,
      view,
      total_view_duration_ms: now - mountTimeRef.current,
      time_since_loaded_ms: loadedAtRef.current
        ? now - loadedAtRef.current
        : null,
      insights_loaded: insightsLoadedRef.current,
      loaded_successfully: loadedSuccessfullyRef.current,
      sections_expanded_count: sectionsExpandedCountRef.current,
      time_expanded_ms: totalTimeExpandedRef.current,
      max_scroll_depth_percent: lifetimeMaxScrollDepthRef.current,
      reached_bottom: everReachedBottomRef.current,
      citation_click_count: linkClickCountRef.current,
    });
  });

  // track view changed
  const previousViewRef = useRef<InsightsPanelView | undefined>(undefined);
  useEffect(() => {
    const now = Date.now();
    if (previousViewRef.current && previousViewRef.current !== view) {
      // Flush in-progress expanded time before emitting
      let timeExpandedMs = totalTimeExpandedRef.current;
      if (anyExpandedSinceRef.current !== null) {
        timeExpandedMs += now - anyExpandedSinceRef.current;
      }

      capture('view_ended', {
        appointmentId,
        view: previousViewRef.current,
        total_view_duration_ms: now - mountTimeRef.current,
        time_since_loaded_ms: loadedAtRef.current
          ? now - loadedAtRef.current
          : null,
        insights_loaded: insightsLoadedRef.current,
        loaded_successfully: loadedSuccessfullyRef.current,
        sections_expanded_count: sectionsExpandedCountRef.current,
        time_expanded_ms: timeExpandedMs,
        max_scroll_depth_percent: lifetimeMaxScrollDepthRef.current,
        reached_bottom: everReachedBottomRef.current,
        citation_click_count: linkClickCountRef.current,
      });

      // Reset per-view counters for the new view
      mountTimeRef.current = now;
      sectionsExpandedCountRef.current = 0;
      totalTimeExpandedRef.current = 0;
      linkClickCountRef.current = 0;
      lifetimeMaxScrollDepthRef.current = 0;
      everReachedBottomRef.current = false;
      if (anyExpandedSinceRef.current !== null) {
        anyExpandedSinceRef.current = now;
      }

      capture('viewed', { appointmentId, view });
    }

    previousViewRef.current = view;
  }, [view, capture, appointmentId]);

  // Track loaded event
  useEffect(() => {
    if (!status || insightsLoadedRef.current) return;

    const loadDurationMs = Date.now() - loadStartTimeRef.current;
    loadedAtRef.current = Date.now();

    if (error) {
      insightsLoadedRef.current = true;
      capture('loaded', {
        appointmentId,
        view,
        success: false,
        error_message: error,
        load_duration_ms: loadDurationMs,
        sections_count: 0,
      });
    } else if (spec) {
      const childKeys = getRootChildKeys(spec);
      if (childKeys.length > 0) {
        insightsLoadedRef.current = true;
        loadedSuccessfullyRef.current = true;
        capture('loaded', {
          appointmentId,
          view,
          success: true,
          load_duration_ms: loadDurationMs,
          component_count: childKeys.length,
        });
      }
    } else if (!spec) {
      insightsLoadedRef.current = true;
      capture('loaded', {
        appointmentId,
        view,
        success: false,
        error_message: 'No insights available',
        load_duration_ms: loadDurationMs,
        sections_count: 0,
      });
    }
  }, [status, spec, error, capture, appointmentId, view]);

  // Scroll depth tracking
  useEffect(() => {
    if (
      !containerRef.current ||
      !bottomMarkerRef.current ||
      !isContentRendered
    ) {
      return;
    }

    const container = containerRef.current;
    const bottomMarker = bottomMarkerRef.current;
    const scrollContainer = getScrollableParent(container);

    if (!scrollContainer) return;

    const checkScrollDepth = () => {
      const markerRect = bottomMarker.getBoundingClientRect();
      const containerRect = scrollContainer.getBoundingClientRect();
      const contentRect = container.getBoundingClientRect();

      const isMarkerVisible = markerRect.top <= containerRect.bottom;

      if (isMarkerVisible) {
        lifetimeMaxScrollDepthRef.current = 100;
        everReachedBottomRef.current = true;
      } else {
        const totalHeight = contentRect.height;
        if (totalHeight > 0) {
          const scrolledAmount = containerRect.bottom - contentRect.top;
          const scrollDepth = Math.round(
            Math.min(100, (scrolledAmount / totalHeight) * 100),
          );
          lifetimeMaxScrollDepthRef.current = Math.max(
            lifetimeMaxScrollDepthRef.current,
            scrollDepth,
          );
        }
      }
    };

    const initialCheckTimeout = setTimeout(checkScrollDepth, 100);
    scrollContainer.addEventListener('scroll', checkScrollDepth, {
      passive: true,
    });

    return () => {
      clearTimeout(initialCheckTimeout);
      scrollContainer.removeEventListener('scroll', checkScrollDepth);
    };
  }, [isContentRendered]);

  return {
    containerRef,
    bottomMarkerRef,
    getAnalyticsState,
    incrementSectionsExpanded,
    addExpandedTime,
    registerExpandedSection,
    incrementLinkClicks,
    getTimeSinceLoaded,
  };
};
